import { ActionIcon, Badge, Group, Table, Text, Tooltip } from "@mantine/core";
import { IconEdit, IconLock, IconPower } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import type {
  PanelBillingMode,
  PanelDefinition,
} from "../../../services/admin/panels.service";

interface PanelAdminRowProps {
  panel: PanelDefinition;
  onEdit: (panel: PanelDefinition) => void;
  onToggleStatus: (panel: PanelDefinition) => void;
}

const BILLING_MODE_LABELS: Record<PanelBillingMode, string> = {
  group_only: "Group only",
  group_plus_individual: "Group + individual",
  individual_only: "Individual only",
};

export function PanelAdminRow({
  panel,
  onEdit,
  onToggleStatus,
}: PanelAdminRowProps) {
  const componentSummary = useMemo(
    () =>
      panel.components
        .map((component) => `${component.stain.code} ×${component.quantity}`)
        .join(", "),
    [panel.components]
  );
  const handleEdit = useCallback(() => onEdit(panel), [onEdit, panel]);
  const handleToggleStatus = useCallback(
    () => onToggleStatus(panel),
    [onToggleStatus, panel]
  );

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <div>
            <Text fw={600} size="sm">
              {panel.name}
            </Text>
            <Text size="xs" c="dimmed">
              Version {panel.currentVersionNumber}
            </Text>
          </div>
          {panel.isSystemManaged && (
            <Tooltip label="Protected system panel">
              <IconLock
                size={16}
                color="var(--mantine-color-blue-6)"
                aria-label="Protected system panel"
              />
            </Tooltip>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={2} title={componentSummary}>
          {componentSummary}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light">{BILLING_MODE_LABELS[panel.billingMode]}</Badge>
        {panel.groupCptCode && (
          <Text size="xs" c="dimmed" mt={4}>
            Group CPT {panel.groupCptCode.code}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Badge
          variant="light"
          color={panel.workflowKind === "tissue_cypher" ? "violet" : "gray"}
        >
          {panel.workflowKind === "tissue_cypher"
            ? "Tissue Cypher"
            : "Ordinary"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color={panel.isActive ? "green" : "gray"}>
          {panel.isActive ? "Active" : "Inactive"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Tooltip label={`Create a new version of ${panel.name}`}>
            <ActionIcon
              size={44}
              variant="subtle"
              aria-label={`Edit ${panel.name}`}
              onClick={handleEdit}
            >
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>
          {!panel.isSystemManaged && (
            <Tooltip
              label={panel.isActive ? "Deactivate panel" : "Activate panel"}
            >
              <ActionIcon
                size={44}
                variant="subtle"
                color={panel.isActive ? "orange" : "green"}
                aria-label={`${panel.isActive ? "Deactivate" : "Activate"} ${
                  panel.name
                }`}
                onClick={handleToggleStatus}
              >
                <IconPower size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
